import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { PageHeader } from '../../components/PageHeader/PageHeader'
import { PageLoadingShell } from '../PageLoadingShell/PageLoadingShell'
import { CreateEntityForm } from '../../components/CreateEntityForm/CreateEntityForm'
import { ConfigTagsInput } from '../../components/ConfigTagsInput/ConfigTagsInput'
import { collectTags } from '../helpers/collectTags'
import { createConfig } from '../../services/api/createConfig'
import { useTenants } from '../../hooks/useTenants'
import { useInventorySystemProfiles } from '../../hooks/useInventorySystemProfiles'
import { useVenues } from '../../hooks/useVenues'
import type { FieldDef, TagRow } from '../../types'
import { ConfigScope } from '../../types'
import { sortSelectOptions } from '../../helpers/sortSelectOptions'
import { isGeneratedSlugIdVenue } from './helpers/isGeneratedSlugIdVenue'

const scopeOptions = Object.values(ConfigScope).map((v) => ({
  value: v,
  label: v,
}))

const COMMON_FIELDS: FieldDef[] = [
  { name: 'configKey', label: 'Config Key', type: 'text' },
  { name: 'configValue', label: 'Config Value', type: 'text' },
  { name: 'configType', label: 'Config Type', type: 'text' },
  {
    name: 'visibleToFrontend',
    label: 'Visible To Frontend',
    type: 'checkbox',
    defaultValue: false,
  },
]

export function CreateConfigPage() {
  const { getToken } = useAuth()
  const navigate = useNavigate()
  const [scope, setScope] = useState<ConfigScope | ''>('')
  const [tagRows, setTagRows] = useState<TagRow[]>([])
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    tenants,
    isLoading: tenantsLoading,
    error: tenantsError,
  } = useTenants()
  const {
    profiles,
    isLoading: profilesLoading,
    error: profilesError,
  } = useInventorySystemProfiles()
  const { venues, isLoading: venuesLoading, error: venuesError } = useVenues()
  const isLoading = tenantsLoading || profilesLoading || venuesLoading
  const loadError = tenantsError ?? profilesError ?? venuesError

  const tenantOptions = useMemo(
    () =>
      tenants
        .map((t) => ({ value: t.code, label: `${t.code} (ID: ${t.id})` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [tenants]
  )

  const ispOptions = useMemo(
    () =>
      profiles
        .map((p) => ({ value: p.code, label: `${p.code} (ID: ${p.id})` }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [profiles]
  )

  const venueOptions = useMemo(
    () =>
      venues
        .filter((v) => !isGeneratedSlugIdVenue(v))
        .map((v) => ({
          value: v.id,
          label: `${v.venueSlug} (${v.inventorySystemType} - ${v.id})`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [venues]
  )

  const fields: FieldDef[] | null = useMemo(() => {
    if (!scope) return null

    const tenantField: FieldDef = {
      name: 'tenantCode',
      label: 'Tenant',
      type: 'select',
      searchable: true,
      options: tenantOptions,
    }
    const ispField: FieldDef = {
      name: 'inventorySystemProfileCode',
      label: 'ISP',
      type: 'select',
      searchable: true,
      options: ispOptions,
    }
    const venueField: FieldDef = {
      name: 'venueId',
      label: 'Venue',
      type: 'select',
      searchable: true,
      options: venueOptions,
    }

    switch (scope) {
      case ConfigScope.IspConfig:
        return [...COMMON_FIELDS, ispField]
      case ConfigScope.TenantConfig:
        return [...COMMON_FIELDS, tenantField]
      case ConfigScope.TenantIspConfig:
        return [...COMMON_FIELDS, tenantField, ispField]
      case ConfigScope.TenantVenueConfig:
        return [...COMMON_FIELDS, tenantField, venueField]
    }
  }, [scope, tenantOptions, ispOptions, venueOptions])

  async function handleSubmit(body: Record<string, unknown>) {
    setSubmitError(null)
    try {
      await createConfig(await getToken(), {
        ...body,
        tags: collectTags(tagRows),
      })
      navigate('/configs')
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to create config'
      )
    }
  }

  return (
    <>
      <PageHeader title="Create Config" />
      <PageLoadingShell
        loadingLabel="config"
        isLoading={isLoading}
        error={loadError}
      >
        <section
          className="content-card content-card--padded"
          aria-label="Create Config"
        >
          {submitError && (
            <div className="alert alert-danger" role="alert">
              <strong>Error:</strong> {submitError}
            </div>
          )}

          <div className="create-form-table mb-3">
            <div className="create-form-row--last">
              <div className="create-form-label">
                Config Scope
                <span className="create-form-required" aria-hidden="true">
                  *
                </span>
              </div>
              <div className="create-form-input-cell">
                <select
                  className="form-select form-select-sm"
                  value={scope}
                  onChange={(e) => setScope(e.target.value as ConfigScope | '')}
                >
                  <option value="">Select…</option>
                  {sortSelectOptions(scopeOptions).map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {fields && (
            <CreateEntityForm
              key={scope}
              fields={fields}
              onSubmit={handleSubmit}
              extraRow={{
                label: 'Tags',
                content: (
                  <ConfigTagsInput rows={tagRows} onChange={setTagRows} />
                ),
              }}
            />
          )}
        </section>
      </PageLoadingShell>
    </>
  )
}
